package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.state.SHOW_REVAMP
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminSyncRunResponse
import twist.mena.sports.shared.dto.admin.OffsetPage

@Composable
fun SyncRunsScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    val isDark = AppTheme.isDark
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminSyncRunResponse>>>(UiState.Loading) }
    // With the revamp hidden, only the legacy sync is shown and it goes unnamed
    var jobNameFilter by remember { mutableStateOf(if (SHOW_REVAMP) null else "legacy_sync") }
    var offset by remember { mutableStateOf(0) }
    var syncTriggering by remember { mutableStateOf<String?>(null) }
    var syncMessage by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }
    var legacySyncEnabled by remember { mutableStateOf<Boolean?>(null) }
    val scope = rememberCoroutineScope()

    // Load schedule settings
    LaunchedEffect(Unit) {
        scope.launch {
            try {
                val schedule = apiClient.getSyncSchedule()
                legacySyncEnabled = schedule["legacy_sync_enabled"]
            } catch (_: Exception) {}
        }
    }

    LaunchedEffect(jobNameFilter, offset, refreshKey) {
        state = UiState.Loading
        scope.launch {
            try {
                val page = apiClient.getSyncRuns(jobNameFilter, offset)
                state = UiState.Success(page)
            } catch (e: Exception) {
                state = UiState.Error("Failed to load sync runs")
            }
        }
    }

    // Sync trigger buttons
    Div({
        style {
            marginBottom(Spacing.lg)
            padding(Spacing.md)
            backgroundColor(Color(colors.surface))
            borderRadius(Spacing.sm)
            property("border", "1px solid ${colors.border}")
        }
    }) {
        Div({ style { marginBottom(Spacing.sm); fontWeight("600"); color(Color(colors.text)) } }) {
            Text("Trigger Sync")
        }
        Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); property("flex-wrap", "wrap"); property("align-items", "center") } }) {
            val syncTypes = if (SHOW_REVAMP) {
                listOf("all" to "Full Sync", "catalog" to "Catalog", "fixtures" to "Fixtures", "standings" to "Standings", "legacy" to "Legacy Sync")
            } else {
                listOf("legacy" to "Sync Now")
            }
            for ((type, label) in syncTypes) {
                val isTriggering = syncTriggering == type
                Button({
                    if (!isTriggering && syncTriggering == null) {
                        onClick {
                            syncTriggering = type
                            syncMessage = null
                            scope.launch {
                                try {
                                    apiClient.triggerSync(type)
                                    syncMessage = if (SHOW_REVAMP) "$label sync started" else "Sync started"
                                    delay(3000)
                                    refreshKey++
                                } catch (e: Exception) {
                                    syncMessage = if (SHOW_REVAMP) "Failed to trigger $label sync" else "Failed to start the sync"
                                } finally {
                                    syncTriggering = null
                                }
                            }
                        }
                    }
                    style {
                        if (type == "all" || !SHOW_REVAMP) primaryButtonStyle(colors, disabled = isTriggering)
                        else secondaryButtonStyle(colors)
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.sm)
                        if (isTriggering || syncTriggering != null) {
                            opacity(0.6)
                            cursor("not-allowed")
                        }
                    }
                }) { Text(if (isTriggering) "Starting..." else label) }
            }
            if (syncMessage != null) {
                Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginLeft(Spacing.sm) } }) {
                    Text(syncMessage!!)
                }
            }
        }
    }

    // Schedule toggle
    if (legacySyncEnabled != null) {
        Div({
            style {
                marginBottom(Spacing.lg)
                padding(Spacing.md)
                backgroundColor(Color(colors.surface))
                borderRadius(Spacing.sm)
                property("border", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                property("align-items", "center")
                gap(Spacing.md)
            }
        }) {
            Span({ style { fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Auto Sync Schedule")
            }
            Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                Text(if (SHOW_REVAMP) "Legacy Sync (daily 05:00 Cairo)" else "Daily at 05:00 Cairo")
            }
            Button({
                onClick {
                    val newValue = !(legacySyncEnabled ?: false)
                    scope.launch {
                        try {
                            val result = apiClient.updateSyncSchedule(mapOf("legacy_sync_enabled" to newValue))
                            legacySyncEnabled = result["legacy_sync_enabled"]
                        } catch (_: Exception) {}
                    }
                }
                style {
                    if (legacySyncEnabled == true) {
                        backgroundColor(Color(if (isDark) "#065f46" else "#d1fae5"))
                        color(Color(if (isDark) "#86efac" else "#065f46"))
                    } else {
                        backgroundColor(Color(if (isDark) "#450a0a" else "#fee2e2"))
                        color(Color(if (isDark) "#fca5a5" else "#b91c1c"))
                    }
                    padding(Spacing.xs, Spacing.md)
                    borderRadius(Spacing.sm)
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.sm)
                    fontWeight("600")
                }
            }) {
                Text(if (legacySyncEnabled == true) "Enabled" else "Disabled")
            }
        }
    }

    // Job name filter
    if (SHOW_REVAMP) Div({ style { marginBottom(Spacing.lg); display(DisplayStyle.Flex); gap(Spacing.sm); property("flex-wrap", "wrap") } }) {
        val jobNames = listOf(null, "reference_data_sync", "daily_fixtures_sync", "daily_standings_sync", "live_poll", "fixture_detail_poll", "legacy_sync")
        val labels = listOf("All", "Reference Data", "Daily Fixtures", "Daily Standings", "Live Poll", "Fixture Detail", "Legacy Sync")

        jobNames.forEachIndexed { index, name ->
            Button({
                onClick {
                    jobNameFilter = name
                    offset = 0
                }
                style {
                    if (jobNameFilter == name) {
                        primaryButtonStyle(colors)
                    } else {
                        secondaryButtonStyle(colors)
                    }
                    padding(Spacing.sm, Spacing.lg)
                    fontSize(Typography.sm)
                }
            }) { Text(labels[index]) }
        }
    }

    when (val s = state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val page = s.data
            val columns = listOf(
                Column<AdminSyncRunResponse>("Job Name") { run -> Text(run.jobName) },
                Column("Status") { run ->
                    val (bgColor, textColor) = when (run.status) {
                        "success" -> Pair(
                            if (isDark) "#052e16" else "#d1fae5",
                            if (isDark) "#86efac" else "#065f46"
                        )
                        "failed" -> Pair(
                            if (isDark) "#450a0a" else "#fee2e2",
                            if (isDark) "#fca5a5" else "#b91c1c"
                        )
                        "running" -> Pair(
                            if (isDark) "#451a03" else "#fff3cd",
                            if (isDark) "#fcd34d" else "#856404"
                        )
                        else -> Pair(colors.surfaceHover, colors.textMuted)
                    }
                    Span({
                        style { badgeStyle(bgColor, textColor) }
                    }) { Text(run.status) }
                },
                Column("Started At") { run ->
                    Span({ style { fontSize(Typography.sm) } }) {
                        Text(run.startedAtUtc.replace("T", " ").take(19))
                    }
                },
                Column("Finished At") { run ->
                    Span({ style { fontSize(Typography.sm) } }) {
                        Text(run.finishedAtUtc?.replace("T", " ")?.take(19) ?: "—")
                    }
                },
                Column("Records") { run -> Text(run.recordsUpdated.toString()) },
                Column("Error") { run ->
                    if (run.error != null) {
                        Span({
                            style { fontSize(Typography.sm); color(Color(colors.danger)); property("max-width", "200px"); overflow("hidden"); property("text-overflow", "ellipsis") }
                        }) { Text(run.error!!) }
                    } else {
                        Text("—")
                    }
                }
            ).let { if (SHOW_REVAMP) it else it.drop(1) }

            DataTable(
                columns = columns,
                rows = page.data,
                total = page.total,
                offset = page.offset,
                limit = page.limit,
                onPageChange = { newOffset -> offset = newOffset }
            )
        }
    }
}
